<template>
    <resource-filter-builder
        class="contacts-filter"
        title="Contact Filters"
        :isActive="hasFilters"
        @filters:apply="filter"
        @filters:reset="resetFilters"
        @filters:cancel="restoreFilters"
    >
        <div class="form-group">
            <label class="contacts-filter__label">Marketing subscription status</label>
            <div class="contact-filter__selected" v-if="filterMarketingSubscriptionStatus">
                <vx-badge
                    color="primary"
                    :data-test="`marketing-subscription-status-${filterMarketingSubscriptionStatus.value}`"
                    :close="true"
                    :tooltip="filterMarketingSubscriptionStatus.label"
                    tooltip-mode="overflow"
                    @close="onMarketingSubscriptionStatusDelete(filterMarketingSubscriptionStatus)"
                >
                    {{ filterMarketingSubscriptionStatus.label }}
                </vx-badge>
            </div>
            <vx-select
                class="contacts-filter__select"
                placeholder="Select contact status"
                data-test="marketing-subscription-status-filter"
                :model-value="null"
                @update:model-value="onMarketingSubscriptionStatusSelect"
                :options="[
                    {
                        label: 'Opted in',
                        value: 'opt_in'
                    },
                    {
                        label: 'Opted out',
                        value: 'opt_out'
                    },
                    {
                        label: 'No preference',
                        value: 'unknown'
                    },
                ]"
                :close-on-select="true"
                :deselect-from-dropdown="true"
                :searchable="false"
                :clearable="false"
            >
            </vx-select>
        </div>
        <div class="form-group">
            <label class="contacts-filter__label">Tags</label>

            <vx-select
                class="contacts-filter__select"
                placeholder="Search contact tags"
                data-test="tags-filter"
                v-model="filterTag"
                :options="tagsOptions"
                :taggable="true"
                :loading="isLoadingTags"
                @search="searchChange"
            >
                <template #no-options>
                    <div class="contacts-filter__select-no-options">Sorry, no matching tags.</div>
                </template>
            </vx-select>
        </div>
    </resource-filter-builder>
</template>

<script setup>
import { ref, computed } from 'vue';
import debounce from '~/utils/debounce';
import ResourceFilterBuilder from '~/components/resource-management/ResourceFilterBuilder.vue';
import tagsClient from '../../services/tagsClient';
import { VxSelect, VxBadge } from '@voxie/frontend-components-v3';
import { useToasted } from '../../composables/useToasted';

const emit = defineEmits(['apply:filters', 'loadCount']);

const $toasted = useToasted();

const teamId = Spark.state.currentTeam.id;

const tagsOptions = ref([]);

const filterStatuses = ref([]);
const appliedFilterStatuses = ref([]);

const filterMarketingSubscriptionStatus = ref(null);
const appliedFilterMarketingSubscriptionStatus = ref(null);

const filterTag = ref(null);
const appliedFilterTag = ref([]);
const isLoadingTags = ref(true);

const hasFilters = computed(() => appliedFilterStatuses.value.length > 0 || appliedFilterTag.value?.length > 0 || appliedFilterMarketingSubscriptionStatus.value !== null);

const onMarketingSubscriptionStatusDelete = () => {
    filterMarketingSubscriptionStatus.value = null;
}

const onMarketingSubscriptionStatusSelect = (status) => {
    filterMarketingSubscriptionStatus.value = status
}

const filter = () => {
    const filters = {};

    if (filterTag.value?.length) {
        filters['filter[tags.tag_name]'] = filterTag.value;
    }

    appliedFilterTag.value = filterTag.value;

    const loadFilters = {};

    if (filterMarketingSubscriptionStatus.value) {
        filters['filter[marketing_subscription_status]'] = filterMarketingSubscriptionStatus.value.value;
        loadFilters['filter[marketing_subscription_status]'] = filterMarketingSubscriptionStatus.value.value;
        appliedFilterMarketingSubscriptionStatus.value = {...filterMarketingSubscriptionStatus.value};
    } else {
        appliedFilterMarketingSubscriptionStatus.value = null;
    }

    emit('apply:filters', filters);
};

const restoreFilters = () => {
    filterStatuses.value = [...appliedFilterStatuses.value];
    filterTag.value = appliedFilterTag.value;
    filterMarketingSubscriptionStatus.value = appliedFilterMarketingSubscriptionStatus.value ? {...appliedFilterMarketingSubscriptionStatus.value} : null;
};

const resetFilters = () => {
    filterStatuses.value = [];
    filterTag.value = null;
    filterMarketingSubscriptionStatus.value = null;
};

const searchTags = (query) => {
    isLoadingTags.value = true;
    tagsClient
        .tagsAutocomplete(teamId, { query })
        .then(({ data }) => {
            tagsOptions.value = data.data.map((tag) => tag.value);
        })
        .catch(() => {
            $toasted.global.platform_error();
        })
        .finally(() => {
            isLoadingTags.value = false;
        });
};

const searchChangeDebounced = debounce((query) => {
    searchTags(query);
}, 350);

const searchChange = (query) => {
    searchChangeDebounced(query);
};
searchTags();
</script>

<style lang="scss">
.contacts-filter {
    .vx-select .vs__dropdown-menu {
        max-height: 326px !important;
    }

    .vx-badge {
        padding: 0 8px;
        border-radius: 10px;
        color: var(--color-grey);
        font-weight: var(--weight-regular);
    }

    .vx-badge__content {
        line-height: 24px;
    }

    div[id^='vx-tooltip'] {
        z-index: 100;
    }
}

.contacts-filter__label {
    font-weight: var(--weight-medium);
    color: var(--color-grey);
}

.contact-filter__selected {
    display: flex;
    flex-wrap: wrap;
    padding-bottom: 8px;
    gap: 8px;
}

.contacts-filter__select-no-options {
    font-family: var(--font-open-sans);
    font-size: 0.75rem;
    padding: 8px;
}
</style>
